import React, { useMemo, useState } from 'react';
import axios from 'axios';
import PaperDoc from './PaperDoc';
import { MenuIcons } from './CardMenu';
import DisplayOptions from './DisplayOptions';
import { fmtMoney, fmtDate } from '../lib/format';

// ═══════════════════════════════════════════════════════════════════════════
//  A LEAD, OPENED
//
//  Julian clicks a card on the board and lands here: the actual estimate, as
//  a sheet of paper, with everything he can do to it in one row above it.
//
//  Before this, clicking a card did nothing you could use. The price lived
//  inside the form it was typed into, so the only way to READ an estimate was
//  to open the editor and scroll the fields — which is how a line priced at
//  $0.00 ships to a customer.
//
//  ── THE SIX BUTTONS ──────────────────────────────────────────────────────
//  Edit · Send · Share · Reminders · PDF/Print · Record Payment
//
//  Record Payment only on an invoice. There is nothing to pay against an
//  estimate, and a button that is there but does nothing is worse than no
//  button: a technician taps it, nothing happens, and he stops trusting the
//  screen.
//
//  ── SEND AND SHARE ARE NOT THE SAME BUTTON ──────────────────────────────
//  Send emails it. Share hands back the link and sends nothing. Julian texts
//  as many estimates as he emails, and a Share that quietly emailed the
//  customer is the kind of surprise that gets a feature abandoned.
// ═══════════════════════════════════════════════════════════════════════════

const METHODS = ['Cash', 'Check', 'Card in person', 'Bank transfer', 'Other'];

// yyyy-mm-dd for <input type="date">, in the browser's own day rather than
// UTC — "received today" typed at 6pm in Utah must not save as tomorrow.
const todayInput = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const addressLines = (r) => [
  r.serviceAddress,
  r.address2,
  [r.city, r.state, r.zip].filter(Boolean).join(', '),
].filter(Boolean);

const billingLines = (r) => (r.billingSame === false
  ? [
    r.billingAddress,
    r.billingAddress2,
    [r.billingCity, r.billingState, r.billingZip].filter(Boolean).join(', '),
  ].filter(Boolean)
  : addressLines(r));

// A lead's estimate row and a lead's invoice row are not the same shape —
// one is { label, cost }, the other { description, unitCost, total }. PaperDoc
// takes one shape, so both are turned into it here rather than PaperDoc
// learning about leads.
const estimateItems = (r) => (r.estimate?.breakdown || []).map(b => ({
  name: b.label || '',
  description: b.description || '',
  qty: Number(b.qty) > 0 ? Number(b.qty) : 1,
  unitCost: Number(b.rate) > 0 ? Number(b.rate) : (Number(b.cost) || 0) / (Number(b.qty) > 0 ? Number(b.qty) : 1),
  files: [],
}));

const invoiceItems = (r) => {
  const rows = r.invoice?.lineItems || [];
  if (rows.length) {
    return rows.map(l => ({
      name: String(l.description || '').split('\n')[0] || 'Work',
      description: String(l.description || '').split('\n').slice(1).join('\n'),
      qty: Number(l.qty) > 0 ? Number(l.qty) : 1,
      unitCost: Number(l.unitCost) || (Number(l.total) || 0) / (Number(l.qty) > 0 ? Number(l.qty) : 1),
      files: [],
    }));
  }
  // An invoice raised as a single figure still has to read as a line.
  return [{ name: r.serviceType || 'Work', description: '', qty: 1, unitCost: Number(r.invoice?.amount) || 0, files: [] }];
};

export default function LeadDoc({ request, authHeaders, onBack, onEdit, onSaved, showToast, permissions }) {
  const hasEstimate = Boolean(request?.estimate?.amount);
  const hasInvoice = request?.invoice?.amount != null;

  // The live document wins. A job that has been billed is a job where the
  // invoice is the thing anyone is asking about; the estimate is history.
  const [kind, setKind] = useState(hasInvoice ? 'invoice' : 'estimate');
  const [sheet, setSheet] = useState(null);     // null | 'send' | 'reminders' | 'pay'
  const [busy, setBusy] = useState(false);

  const doc = kind === 'invoice' ? request.invoice : request.estimate;

  // What the job has actually taken, by any route. A failed bank payment and
  // a lost chargeback are not money in hand.
  const paid = useMemo(() => {
    const online = (request.onlinePayments || [])
      .filter(p => p.status === 'succeeded' && p.dispute?.status !== 'lost')
      .reduce((t, p) => t + (Number(p.amount) || 0), 0);
    const offline = (request.offlinePayments || [])
      .reduce((t, p) => t + (Number(p.amount) || 0), 0);
    // Money given back is money the job no longer has. A pending refund is
    // not off yet and can still fail, so it does not count here.
    const back = (request.refunds || [])
      .filter(r => r.status === 'succeeded')
      .reduce((t, r) => t + (Number(r.amount) || 0), 0);
    return Math.round((online + offline - back) * 100) / 100;
  }, [request.onlinePayments, request.offlinePayments, request.refunds]);

  const due = Math.round((Number(request.invoice?.amount) || 0) * 100) / 100;
  const balance = Math.round((due - paid) * 100) / 100;

  // Card payments with money still on them. Cash cannot be refunded through
  // Stripe, so it is not offered: a cash refund is cash out of a pocket and
  // belongs on the Record Payment sheet as a negative, not here.
  const refundable = useMemo(() => (request.onlinePayments || [])
    .filter(p => p.status === 'succeeded' && p.dispute?.status !== 'lost' && p.stripePaymentIntentId)
    .map((p) => {
      const given = (request.refunds || [])
        .filter(r => String(r.paymentId) === String(p._id) && r.status !== 'failed' && r.status !== 'canceled')
        .reduce((t, r) => t + (Number(r.amount) || 0), 0);
      return { ...p, given: Math.round(given * 100) / 100, left: Math.round((p.amount - given) * 100) / 100 };
    })
    .filter(p => p.left > 0.005), [request.onlinePayments, request.refunds]);

  // Unknown means an older sign-in that predates permissions being sent. The
  // button shows, the server decides, and the refusal says what to do about
  // it. Better than hiding a button an owner is entitled to press.
  const mayRefund = !permissions || permissions.includes('refunds');

  const post = async (path, body) => {
    setBusy(true);
    try {
      const res = await axios.post(`/api/requests/${request._id}${path}`, body, authHeaders());
      if (res.data?.request) onSaved?.(res.data.request);
      return res.data;
    } catch (err) {
      alert(err.response?.data?.message || err.message);
      return null;
    } finally {
      setBusy(false);
    }
  };

  // Refunds live on /api/pay, not /api/requests, because that is where the
  // Stripe work is. Same shape of answer, same error handling.
  const payRefund = async (body) => {
    setBusy(true);
    try {
      const res = await axios.post('/api/pay/refund', { requestId: request._id, ...body }, authHeaders());
      if (res.data?.request) onSaved?.(res.data.request);
      return res.data;
    } catch (err) {
      alert(err.response?.data?.message || err.message);
      return null;
    } finally {
      setBusy(false);
    }
  };

  // The link, onto the clipboard. The prompt fallback is there because the
  // clipboard API refuses outright in some in-app browsers, and a Share that
  // silently does nothing is a Share nobody presses twice.
  const share = async () => {
    const res = await post('/link', { kind });
    if (!res?.link) return;
    try {
      await navigator.clipboard.writeText(res.link);
      showToast?.(`${kind === 'invoice' ? 'Pay' : 'Estimate'} link copied. Paste it into a text or email.`);
    } catch {
      window.prompt('Copy this link:', res.link);
    }
  };

  const [optionsOpen, setOptionsOpen] = useState(false);

  const actions = [
    { key: 'edit', label: 'Edit', icon: MenuIcons.pencil, onClick: () => onEdit?.(kind) },
    { key: 'send', label: 'Send', icon: MenuIcons.send, onClick: () => setSheet('send') },
    { key: 'share', label: 'Share', icon: MenuIcons.share, onClick: share },
    { key: 'remind', label: 'Reminders', icon: MenuIcons.bell, onClick: () => setSheet('reminders') },
    { key: 'print', label: 'PDF / Print', icon: MenuIcons.printer, onClick: () => window.print() },
    // ── WHICH COLUMNS THIS SHEET SHOWS ──
    // The switches existed and were reachable only from the OTHER document
    // component, so from here they were invisible and this sheet ignored
    // them anyway. Both halves of that are fixed: PaperDoc honours them now,
    // and this is the way in from the document Julian actually looks at.
    { key: 'cols', label: 'Columns', icon: MenuIcons.eye, onClick: () => setOptionsOpen(true) },
    // Nothing to pay against an estimate.
    { key: 'pay', label: 'Record Payment', icon: MenuIcons.dollar, hidden: kind !== 'invoice', onClick: () => setSheet('pay') },
    // Only where there is card money to give back, and only for somebody
    // allowed to give it. Nothing to refund on an estimate or on cash.
    {
      key: 'refund',
      label: 'Refund',
      icon: MenuIcons.undo,
      hidden: kind !== 'invoice' || refundable.length === 0 || !mayRefund,
      onClick: () => setSheet('refund'),
    },
  ];

  const status = kind === 'invoice'
    ? (request.invoiceStatus === 'paid' ? 'PAID' : paid > 0 ? 'PART PAID' : 'UNPAID')
    : (request.estimateStatus === 'approved' ? 'APPROVED'
      : request.estimateStatus === 'rejected' ? 'DECLINED'
        : request.estimateStatus === 'pending' ? 'SENT' : 'DRAFT');

  // Nothing written yet. Said plainly, with the one button that fixes it,
  // rather than an empty sheet that reads like a bug.
  if (!hasEstimate && !hasInvoice) {
    return (
      <div className="pd ld-empty-wrap">
        <div className="pd-bar print-hide">
          <button type="button" className="pd-bar-btn" onClick={onBack}>‹ Leads</button>
          <span className="pd-bar-title">{request.fullName || 'Lead'}</span>
          <span className="pd-bar-btn ghost" aria-hidden="true" />
        </div>
        <div className="ld-empty">
          <p>Nothing has been written for this job yet.</p>
          <button type="button" className="btn btn-primary" onClick={() => onEdit?.('estimate')}>Write the estimate</button>
        </div>
      </div>
    );
  }

  const sends = (request.sends || []).filter(s => s.kind === kind);
  // The most recent send, and only if THAT one bounced. A bounce followed by
  // a send to a corrected address is a solved problem, and a band still
  // shouting about it would be the next thing nobody reads.
  const last = sends[sends.length - 1];
  const bounced = last && last.status === 'bounced' ? last : null;

  return (
    <>
      <PaperDoc
        kind={kind}
        backLabel="Work Order Status"
        // An estimate carries its own number now; the invoice tab keeps
        // showing the work order's, which is what it always showed.
        number={(kind === 'estimate' ? request.estimate?.number : '') || request.workOrder?.number || ''}
        // The date Julian put on the estimate, when he put one there. Falls
        // back to the day it was sent, which is every document written
        // before the field existed.
        date={doc?.docDate || doc?.submittedAt || request.updatedAt}
        status={status}
        // QPS on the letterhead, the entity underneath it. The name a
        // customer reads is the short one; the name that holds the Utah
        // contractor licence this work is sold under still has to appear on
        // the document, which is what the line below it is for.
        from={{
          name: 'QPS',
          lines: ['Quick Property Services LLC', 'Utah', '(801) 613-0482', 'billing@qpshub.com'],
        }}
        to={{ name: request.fullName || '', lines: billingLines(request) }}
        serviceAddress={addressLines(request).join(', ')}
        items={kind === 'invoice' ? invoiceItems(request) : estimateItems(request)}
        notes={doc?.notes}
        settlement={kind === 'invoice' && paid > 0 ? { paid, balance } : null}
        terms={kind === 'invoice' ? 'Due upon receipt. Quick Property Services LLC.' : ''}
        onBack={onBack}
        actions={actions}
        busy={busy}
        tabs={{
          value: kind,
          onChange: setKind,
          options: [
            hasEstimate && { key: 'estimate', label: 'Estimate' },
            hasInvoice && { key: 'invoice', label: 'Invoice' },
          ].filter(Boolean),
        }}
        bannerSlot={
          // What the board cannot tell him: whether the customer ever opened
          // it. "Sent and never opened" is a wrong email address, and that is
          // a phone call, not another reminder.
          // A bounce outranks everything else this band could say. "Sent
          // twice, not opened yet" on mail that was refused at the door sent
          // Julian chasing a customer who had never been written to.
          bounced ? (
            <div className="ld-band print-hide bad">
              This {kind} did not arrive. {bounced.to} refused it
              {bounced.statusDetail ? `: ${bounced.statusDetail}` : '.'} Fix the
              address with Edit customer and send it again, or copy the link and text it.
            </div>
          ) : kind === 'estimate' && sends.length > 0 ? (
            <div className="ld-band print-hide">
              Sent {sends.length === 1 ? 'once' : `${sends.length} times`}, last on {fmtDate(sends[sends.length - 1].at)}
              {request.estimateShare?.viewCount
                ? ` · opened ${request.estimateShare.viewCount === 1 ? 'once' : `${request.estimateShare.viewCount} times`}`
                : ' · not opened yet'}
            </div>
          ) : null
        }
      />

      {/* The column switches, from the document they apply to. */}
      <DisplayOptions open={optionsOpen} onClose={() => setOptionsOpen(false)} />

      {sheet === 'send' && (
        <SendSheet
          request={request} kind={kind} busy={busy}
          onClose={() => setSheet(null)}
          onSend={(message) => post('/send', { kind, message })}
        />
      )}

      {sheet === 'reminders' && (
        <RemindersSheet
          sends={sends} kind={kind} busy={busy}
          onClose={() => setSheet(null)}
          onRemind={() => post('/send', { kind, reminder: true })}
        />
      )}

      {sheet === 'pay' && (
        <RecordPaymentSheet
          due={due} paid={paid} balance={balance} busy={busy}
          onClose={() => setSheet(null)}
          onRecord={(body) => post('/payment', body)}
        />
      )}

      {sheet === 'refund' && (
        <RefundSheet
          payments={refundable} busy={busy}
          onClose={() => setSheet(null)}
          onRefund={(body) => payRefund(body)}
        />
      )}
    </>
  );
}

// ── What became of one send ────────────────────────────────────────────────
//
// "Sent" only ever meant the mail provider took it. The customer's own server
// gets it next and refuses an unknown mailbox minutes later. Paul at Alpine
// Fitness never saw his estimate and this screen read Sent for a week.
//
// So the row says what actually happened. A bounce is the loudest word here
// because it is the only one that needs Julian to do something.
const sendWord = (s) => {
  if (s.status === 'bounced') return 'DID NOT ARRIVE';
  if (s.status === 'complained') return 'Marked as spam';
  if (s.status === 'delayed') return 'Still trying';
  if (s.status === 'delivered') return s.reminder ? 'Reminder delivered' : 'Delivered';
  if (!s.emailed) return 'Link made';
  return s.reminder ? 'Reminder sent' : 'Sent';
};

// ── Send ───────────────────────────────────────────────────────────────────
//
// Shows the address it is going to BEFORE it goes. Julian spent a morning
// wondering why an invoice never arrived, and the answer was a mailbox that
// did not exist. A screen that says "Sent" without ever showing where would
// have cost him that morning again.
function SendSheet({ request, kind, busy, onClose, onSend }) {
  const [message, setMessage] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const label = kind === 'invoice' ? 'invoice' : 'estimate';
  const to = request.email;

  const go = async () => {
    const res = await onSend(message);
    if (res) setResult(res);
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(result.link); setCopied(true); }
    catch { window.prompt('Copy this link:', result.link); }
  };

  return (
    <div className="modal active stc" role="dialog" aria-modal="true">
      <div className="stc-scrim" onClick={onClose} />
      <div className="modal-box stc-box">
        <div className="modal-handle" />
        {!result ? (
          <>
            <h2 className="stc-h">Send this {label}</h2>
            <p className="stc-sub">
              {to
                ? <>It goes to <b>{to}</b>. Wrong address? Close this and use Edit customer.</>
                : <>There is no email address on this job. Send it anyway and you get a link to text them.</>}
            </p>
            {kind === 'estimate' && (
              <div className="form-group">
                <label>A note for them <span className="stc-opt">(optional)</span></label>
                <textarea rows={3} value={message} onChange={e => setMessage(e.target.value)}
                  placeholder="Thanks for having us out today. Here is the price we talked about." />
              </div>
            )}
            <button type="button" className="stc-send" onClick={go} disabled={busy}>
              {busy ? 'Sending…' : `Send ${label}`}
            </button>
            <button type="button" className="stc-cancel" onClick={onClose}>Not now</button>
          </>
        ) : (
          <>
            <div className="stc-tick">{result.emailed ? '✓' : '!'}</div>
            <h2 className="stc-h center">{result.emailed ? 'Sent' : 'Ready to send'}</h2>
            <p className="stc-sub center">{result.message}</p>
            {/* The mail provider's own words, for Julian only. Every failure
                used to read the same from here, and an unverified domain, a
                key from the wrong account and a plan that will not deliver to
                this recipient have three different fixes. */}
            {!result.emailed && result.reason && (
              <div className="ld-why">{result.reason}</div>
            )}
            <label className="stc-link-l">Their link</label>
            <input className="stc-link" readOnly value={result.link} onFocus={e => e.target.select()} />
            <button type="button" className="stc-send" onClick={copy}>{copied ? 'Copied' : 'Copy link'}</button>
            <p className="stc-keep">
              This link keeps working. Send it again and they get the same one, so a
              customer who comes back to it next week still finds it.
            </p>
            <button type="button" className="stc-cancel" onClick={onClose}>Done</button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Reminders ──────────────────────────────────────────────────────────────
//
// What a reminder IS here: the same document, sent again, worded as a nudge.
// Not a schedule. A scheduler that fires on its own would need to know when
// to stop, and nobody has told it — so the decision to chase stays with the
// person who knows whether they already phoned.
function RemindersSheet({ sends, kind, busy, onClose, onRemind }) {
  const [sent, setSent] = useState(0);
  const label = kind === 'invoice' ? 'invoice' : 'estimate';

  return (
    <div className="modal active stc" role="dialog" aria-modal="true">
      <div className="stc-scrim" onClick={onClose} />
      <div className="modal-box stc-box">
        <div className="modal-handle" />
        <h2 className="stc-h">Reminders</h2>
        {sends.length === 0 ? (
          <p className="stc-sub">This {label} has not been sent yet. Use Send first.</p>
        ) : (
          <ul className="ld-sends">
            {sends.map((s, i) => (
              <li key={i} className={s.status === 'bounced' || s.status === 'complained' ? 'bad' : ''}>
                <b>{sendWord(s)}</b> {fmtDate(s.at)}
                <span>{s.emailed || s.to ? ` to ${s.to}` : ' — link only, no email went out'}</span>
                {/* What the customer's own mail server said. "Unknown
                    mailbox" is a typo to fix; "mailbox full" is not. */}
                {s.statusDetail && <span className="ld-send-why">{s.statusDetail}</span>}
              </li>
            ))}
          </ul>
        )}
        {sent > 0 && <p className="stc-sub center">Reminder sent.</p>}
        <button type="button" className="stc-send" disabled={busy || sends.length === 0}
          onClick={async () => { const r = await onRemind(); if (r) setSent(n => n + 1); }}>
          {busy ? 'Sending…' : 'Send a reminder now'}
        </button>
        <button type="button" className="stc-cancel" onClick={onClose}>Done</button>
      </div>
    </div>
  );
}

// ── Refund ─────────────────────────────────────────────────────────────────
//
// The only screen in this app that sends money out, so it is built to be hard
// to do by accident and impossible to do by mistake:
//
//   · the payment is picked, never assumed. A job can take two cards.
//   · the amount starts at what is LEFT on that payment, and anything larger
//     is refused by the server rather than quietly trimmed.
//   · the reason is required. Six months later "why did we refund $574?" has
//     to have an answer on the record.
//   · the last step says the figure back in words before it goes.
//
// It also says the five-to-ten-day wait out loud, because the customer will
// ring about it on day two otherwise.
function RefundSheet({ payments, busy, onClose, onRefund }) {
  const [paymentId, setPaymentId] = useState(payments[0]?._id || '');
  const chosen = payments.find(p => String(p._id) === String(paymentId)) || payments[0];
  const [amount, setAmount] = useState(chosen ? chosen.left.toFixed(2) : '');
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [problem, setProblem] = useState('');
  const [done, setDone] = useState(null);

  const pick = (id) => {
    const p = payments.find(x => String(x._id) === String(id));
    setPaymentId(id);
    setAmount(p ? p.left.toFixed(2) : '');
    setProblem('');
    setConfirming(false);
  };

  const asked = Number(amount);
  const valid = Number.isFinite(asked) && asked > 0 && chosen && asked <= chosen.left + 0.005;

  const review = () => {
    if (!chosen) { setProblem('Pick which payment to refund.'); return; }
    if (!Number.isFinite(asked) || asked <= 0) { setProblem('Type how much to give back.'); return; }
    if (asked > chosen.left + 0.005) {
      setProblem(`That payment has ${fmtMoney(chosen.left)} left on it.`);
      return;
    }
    if (reason.trim().length < 3) { setProblem('Say why. This goes on the record.'); return; }
    setProblem('');
    setConfirming(true);
  };

  const go = async () => {
    const res = await onRefund({ paymentId, amount: asked, reason: reason.trim() });
    if (res) setDone(res);
    else setConfirming(false);
  };

  return (
    <div className="modal active stc" role="dialog" aria-modal="true">
      <div className="stc-scrim" onClick={onClose} />
      <div className="modal-box stc-box">
        <div className="modal-handle" />
        {done ? (
          <>
            <div className="stc-tick">✓</div>
            <h2 className="stc-h center">Refund sent</h2>
            <p className="stc-sub center">
              {fmtMoney(done.refund?.amount)} is on its way back to the card.
              It takes five to ten business days to show on their statement.
              We have emailed them to say so.
            </p>
            <button type="button" className="stc-cancel" onClick={onClose}>Done</button>
          </>
        ) : confirming ? (
          <>
            <h2 className="stc-h">Give back {fmtMoney(asked)}?</h2>
            <p className="stc-sub">
              This sends real money out of the account. It cannot be taken back.
            </p>
            <ul className="rf-confirm">
              <li><span>Refunding</span><strong>{fmtMoney(asked)}</strong></li>
              <li><span>Out of</span><strong>{chosen.method || 'Card'} · {fmtMoney(chosen.amount)}</strong></li>
              {chosen.vendorId && (
                <li><span>The technician&rsquo;s share</span><strong>comes back out too</strong></li>
              )}
              <li><span>Reason</span><strong>{reason.trim()}</strong></li>
            </ul>
            <button type="button" className="stc-send rf-send" onClick={go} disabled={busy}>
              {busy ? 'Sending…' : `Yes, refund ${fmtMoney(asked)}`}
            </button>
            <button type="button" className="stc-cancel" onClick={() => setConfirming(false)}>
              Go back
            </button>
          </>
        ) : (
          <>
            <h2 className="stc-h">Give money back</h2>
            <p className="stc-sub">
              It goes back to the card they paid with. There is no other way to
              send it, and it cannot go to a different card.
            </p>

            {payments.length > 1 && (
              <div className="form-group">
                <label>Which payment</label>
                <select value={paymentId} onChange={e => pick(e.target.value)}>
                  {payments.map(p => (
                    <option key={p._id} value={p._id}>
                      {fmtMoney(p.amount)} · {p.method || 'Card'} · {fmtDate(p.paidAt || p.createdAt)}
                      {p.given > 0 ? ` · ${fmtMoney(p.left)} left` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {chosen && (
              <div className="rf-left">
                {fmtMoney(chosen.left)} left to give back
                {chosen.given > 0 ? ` · ${fmtMoney(chosen.given)} already refunded` : ''}
              </div>
            )}

            <div className="form-group">
              <label>How much <span className="req">*</span></label>
              <input type="number" inputMode="decimal" step="0.01" min="0" value={amount}
                onChange={e => { setAmount(e.target.value); setProblem(''); }} placeholder="0.00" />
            </div>

            <div className="form-group">
              <label>Why <span className="req">*</span></label>
              <textarea rows={2} value={reason}
                onChange={e => { setReason(e.target.value); setProblem(''); }}
                placeholder="We did the wrong driveway." />
            </div>

            {problem && <div className="stc-problem">{problem}</div>}
            <button type="button" className="stc-send" onClick={review} disabled={busy || !valid}>
              Review it
            </button>
            <button type="button" className="stc-cancel" onClick={onClose}>Not now</button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Record Payment ─────────────────────────────────────────────────────────
//
// The man who hands over four hundred dollars in twenties. No amount of
// online payment machinery covers him, and until this existed his invoice
// said UNPAID for ever.
function RecordPaymentSheet({ due, paid, balance, busy, onClose, onRecord }) {
  // Prefilled with what is left, because that is the figure nine times in ten
  // and retyping it is nine chances to mistype it.
  const [f, setF] = useState({
    amount: balance > 0 ? String(balance.toFixed(2)) : '',
    method: 'Cash',
    receivedAt: todayInput(),
    reference: '',
    note: '',
  });
  const [problem, setProblem] = useState('');
  const [done, setDone] = useState(null);
  const set = (patch) => { setProblem(''); setF(p => ({ ...p, ...patch })); };

  const go = async () => {
    const amount = Number(f.amount);
    if (!Number.isFinite(amount) || amount <= 0) { setProblem('Type how much was paid.'); return; }
    const res = await onRecord({ ...f, amount });
    if (res) setDone(res);
  };

  return (
    <div className="modal active stc" role="dialog" aria-modal="true">
      <div className="stc-scrim" onClick={onClose} />
      <div className="modal-box stc-box">
        <div className="modal-handle" />
        {!done ? (
          <>
            <h2 className="stc-h">Record a payment</h2>
            <p className="stc-sub">
              Money taken outside the app: cash, a check, a card you ran yourself.
              Invoice {fmtMoney(due)} · paid {fmtMoney(paid)} · left {fmtMoney(balance)}
            </p>

            <div className="form-group">
              <label>How much <span className="req">*</span></label>
              <input type="number" inputMode="decimal" step="0.01" min="0" value={f.amount}
                onChange={e => set({ amount: e.target.value })} placeholder="0.00" />
            </div>

            <div className="form-group">
              <label>How they paid</label>
              <select value={f.method} onChange={e => set({ method: e.target.value })}>
                {METHODS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label>Day it was received</label>
              <input type="date" value={f.receivedAt} onChange={e => set({ receivedAt: e.target.value })} />
            </div>

            <div className="form-group">
              <label>Check number or last four <span className="stc-opt">(optional)</span></label>
              <input value={f.reference} onChange={e => set({ reference: e.target.value })} placeholder="1042" />
            </div>

            <div className="form-group">
              <label>Note <span className="stc-opt">(optional)</span></label>
              <textarea rows={2} value={f.note} onChange={e => set({ note: e.target.value })}
                placeholder="Paid at the door, receipt given." />
            </div>

            {problem && <div className="stc-problem">{problem}</div>}
            <button type="button" className="stc-send" onClick={go} disabled={busy}>
              {busy ? 'Saving…' : 'Record it'}
            </button>
            <button type="button" className="stc-cancel" onClick={onClose}>Not now</button>
          </>
        ) : (
          <>
            <div className="stc-tick">✓</div>
            <h2 className="stc-h center">{done.settled ? 'Invoice paid in full' : 'Payment recorded'}</h2>
            <p className="stc-sub center">
              {done.settled
                ? `${fmtMoney(done.paid)} of ${fmtMoney(done.due)}. Nothing left on this job.`
                : `${fmtMoney(done.paid)} of ${fmtMoney(done.due)}. ${fmtMoney(done.balance)} still to come.`}
            </p>
            <button type="button" className="stc-cancel" onClick={onClose}>Done</button>
          </>
        )}
      </div>
    </div>
  );
}
